import { useState } from 'react';
import { Button, Page } from 'konsta/react';
import { CheckCircle2 } from 'lucide-react';
import { clientProfileSchema } from '@harmony/shared';
import { api, type City, type ClientProfile } from '../api';
import { BottomActions, Field, GenderPicker, LocationField, PhoneField, TextInput, type LocationValue } from '../components/form';
import { haptic } from '../telegram';
import { useTelegramBack } from '../hooks';
import { useErrorText, useName, useT } from '../i18n';

type Form = { displayName: string; gender?: 'MALE' | 'FEMALE'; phone: string; location: LocationValue };

/** Client sign-up: one form, a confirmation screen (Done / Edit), then a success screen. */
export function ClientRegistration({ cities, defaultName, onDone }: { cities: City[]; defaultName: string; onDone: (p: ClientProfile) => void }) {
  const t = useT();
  const name = useName();
  const errText = useErrorText();
  const [form, setForm] = useState<Form>({ displayName: defaultName, phone: '', location: {} });
  const [stage, setStage] = useState<'form' | 'confirm' | 'done'>('form');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<ClientProfile | null>(null);

  useTelegramBack(stage === 'confirm' ? () => setStage('form') : null);

  /** Updates one field and clears its error message. */
  const edit = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: '' }));
  };

  const review = () => {
    const r = clientProfileSchema.safeParse(form);
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const i of r.error.issues) e[String(i.path[0])] ??= errText(i.message === 'Required' ? 'REQUIRED' : i.message);
      if (!form.gender) e.gender = errText('REQUIRED');
      setErrors(e);
      haptic('error');
      return;
    }
    setErrors({});
    setForm({ ...form, phone: r.data.phone });
    setStage('confirm');
  };

  const submit = async () => {
    setBusy(true);
    try {
      const p = await api.createClient(form);
      haptic('success');
      setSaved(p);
      setStage('done');
    } catch (e) {
      setErrors({ submit: errText(e) });
      setStage('form');
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'done' && saved) {
    return (
      <Page className="flex flex-col items-center justify-center px-8 text-center">
        <CheckCircle2 size={72} className="mb-4 text-green-500" />
        <h1 className="mb-8 text-2xl font-semibold">{t('reg.client_success')}</h1>
        <Button large rounded onClick={() => onDone(saved)}>
          {t('app.continue')}
        </Button>
      </Page>
    );
  }

  if (stage === 'confirm') {
    const cityName = form.location.cityId ? name(cities.find((c) => c.id === form.location.cityId)) : t('reg.location_saved');
    const rows: Array<[string, string]> = [
      [t('reg.name'), form.displayName],
      [t('reg.gender'), t(`gender.${form.gender!}`)],
      [t('reg.phone'), form.phone],
      [t('reg.location'), cityName],
    ];
    return (
      <Page className="flex flex-col">
        <div className="flex-1 px-5 pt-8">
          <h1 className="mb-6 text-2xl font-semibold">{t('reg.confirm_title')}</h1>
          <div className="divide-y divide-tg-secondary overflow-hidden rounded-2xl bg-tg-secondary">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 bg-tg-bg px-4 py-3.5">
                <span className="text-tg-hint">{k}</span>
                <span className="text-right font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <BottomActions>
          <Button large rounded disabled={busy} onClick={submit}>
            {t('reg.done')}
          </Button>
          <Button large rounded clear onClick={() => setStage('form')}>
            {t('reg.edit')}
          </Button>
        </BottomActions>
      </Page>
    );
  }

  return (
    <Page className="flex flex-col">
      <div className="flex-1 px-5 pt-8">
        <h1 className="mb-6 text-2xl font-semibold">{t('reg.client_title')}</h1>
        <Field label={t('reg.name')} hint={t('reg.name_hint')} error={errors.displayName}>
          <TextInput value={form.displayName} maxLength={40} onChange={(e) => edit('displayName', e.target.value)} />
        </Field>
        <Field label={t('reg.gender')} error={errors.gender}>
          <GenderPicker value={form.gender} onChange={(gender) => edit('gender', gender)} />
        </Field>
        <Field label={t('reg.phone')} error={errors.phone}>
          <PhoneField value={form.phone} onChange={(phone) => edit('phone', phone)} />
        </Field>
        <Field label={t('reg.location')} error={errors.location}>
          <LocationField cities={cities} value={form.location} onChange={(location) => edit('location', location)} />
        </Field>
        {errors.submit && <p className="mb-4 text-red-500">{errors.submit}</p>}
      </div>
      <BottomActions>
        <Button large rounded onClick={review}>
          {t('reg.next')}
        </Button>
      </BottomActions>
    </Page>
  );
}
